import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  useTheme,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Heart, Link as LinkIcon, Plus, Activity, Droplet, Thermometer, Unlink, Clock, Stethoscope, User } from 'lucide-react';
import agtfitService from '../service/agtfitService';
import AppleSpinner from '../../common/AppleSpinner';

// Constantes de estilo
const FIT_ORANGE = '#FF6B00';
const GRADIENT = 'linear-gradient(90deg, #FF6B00 0%, #FF9800 100%)';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Animation keyframes for gradient movement (currently unused)
// const moveGradient = keyframes`
//   0% {
//     background-position: 0% 50%;
//   }
//   50% {
//     background-position: 100% 50%;
//   }
//   100% {
//     background-position: 0% 50%;
//   }
// `;

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  padding: theme.spacing(3),
  height: '100%',
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 30px rgba(0,0,0,0.08)',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.98)'),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

// Título con gradiente
const TitleText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  textAlign: 'center',
  width: '100%',
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  backgroundImage: GRADIENT,
  color: 'white',
  fontWeight: 600,
  borderRadius: 16,
  padding: '12px 30px',
  marginTop: theme.spacing(3),
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 4px 15px rgba(255,107,0,0.25)',
  textTransform: 'none',
  '&:hover': {
    backgroundImage: GRADIENT,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(255,107,0,0.3)',
  }
}));

const OutlinedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  borderRadius: 16,
  padding: '8px 24px',
  marginTop: theme.spacing(3),
  transition: 'all 0.2s ease-in-out',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.error.light + '20',
    borderColor: theme.palette.error.dark,
    boxShadow: '0 2px 8px rgba(255,59,48,0.15)',
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(3, 0),
}));

const DataContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  textAlign: 'left',
  marginTop: theme.spacing(3),
}));

const VitalSign = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  transition: 'all 0.2s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 15px rgba(0,0,0,0.05)',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  color: FIT_ORANGE,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 15px rgba(0,0,0,0.03)',
  }
}));

// Función para capitalizar primera letra
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const Health = ({ selectedDate }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  
  // Verificar si el usuario ya está conectado al cargar el componente
  useEffect(() => {
    const checkConnection = async () => {
      setProfileLoading(true);
      
      try {
        // Verificar el estado de conexión en el perfil del usuario
        const profileResponse = await agtfitService.getProfile();
        
        if (profileResponse.success && profileResponse.data) {
          const isHealthConnected = profileResponse.data.healthConnected === true;
          
          // Si el perfil indica que está conectado, intentar cargar los datos de salud
          if (isHealthConnected) {
            setIsConnected(true);
            setLoading(true);
            
            try {
              // Intentar cargar los datos de salud automáticamente
              const response = await agtfitService.connectHealthData();
              
              if (response.success) {
                setHealthData(response.data);
                // Actualizar localStorage para mantener coherencia
                localStorage.setItem('agtfit_health_connected', 'true');
              } else {
                // Si hay un error al cargar los datos, desconectar al usuario
                setError('Could not load health data. Please try connecting again.');
                localStorage.removeItem('agtfit_health_connected');
                setIsConnected(false);
                
                // Desactivar la conexión en el perfil
                await agtfitService.updateProfile({ healthConnected: false });
              }
            } catch (err) {
              console.error('Error loading health data:', err);
              setError('An error occurred while loading your health data.');
              setIsConnected(false);
              await agtfitService.updateProfile({ healthConnected: false });
            } finally {
              setLoading(false);
            }
          } else {
            // Si el perfil indica que no está conectado, verificar localStorage para coherencia
            if (localStorage.getItem('agtfit_health_connected') === 'true') {
              localStorage.removeItem('agtfit_health_connected');
            }
          }
        } else {
          // Si no hay perfil o hay un error al obtenerlo, limpiar localStorage
          localStorage.removeItem('agtfit_health_connected');
        }
      } catch (err) {
        console.error('Error checking profile connection status:', err);
      } finally {
        setProfileLoading(false);
      }
    };
    
    checkConnection();
  }, []);
  
  // Función para extraer valor de signos vitales
  const extractVitalSignValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object' && 'S' in value) return value.S;
    return value;
  };
  
  // Función para obtener signos vitales del registro más reciente
  const getLatestVitalSigns = (records) => {
    if (!records || !records.length) return {};
    
    // Ordenar registros por fecha, más reciente primero
    const sortedRecords = [...records].sort((a, b) => 
      new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)
    );
    
    // Buscar el registro más reciente con signos vitales
    for (const record of sortedRecords) {
      if (record.vitalSigns) {
        try {
          // Puede ser un string JSON o un objeto
          const vitalSigns = typeof record.vitalSigns === 'string' 
            ? JSON.parse(record.vitalSigns) 
            : record.vitalSigns;
          
          // Extraer valores
          const processedVitalSigns = {};
          Object.entries(vitalSigns).forEach(([key, value]) => {
            processedVitalSigns[key] = extractVitalSignValue(value);
          });
          
          return processedVitalSigns;
        } catch (e) {
          console.error('Error processing record vitalSigns:', e);
        }
      }
    }
    
    return {};
  };
  
  const handleConnect = async () => {
    setLoading(true);
    setError(null);
  
    try {
      // Primero actualizar el perfil para establecer healthConnected en true
      const profileUpdateResponse = await agtfitService.updateProfile({ healthConnected: true });
  
      if (!profileUpdateResponse.success) {
        throw new Error(profileUpdateResponse.message || 'Failed to update profile');
      }
  
      // Luego conectar los datos de salud
      const response = await agtfitService.connectHealthData();
  
      if (response.success) {
        setHealthData(response.data);
        setIsConnected(true);
  
        // Guardar el estado de conexión en localStorage
        localStorage.setItem('agtfit_health_connected', 'true');
      } else {
        setError(response.message || 'Failed to connect to health data');
  
        // Si falla la conexión, revertir el estado en el perfil
        await agtfitService.updateProfile({ healthConnected: false });
      }
    } catch (err) {
      console.error('Error connecting to health data:', err);
      setError('An unexpected error occurred. Please try again later.');
  
      // Revertir el estado en el perfil si hay un error
      await agtfitService.updateProfile({ healthConnected: false });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      
      // Eliminar datos y estado de conexión
      setHealthData(null);
      setIsConnected(false);
      localStorage.removeItem('agtfit_health_connected');
      
      // Actualizar el perfil del usuario para desactivar la conexión
      await agtfitService.updateProfile({ healthConnected: false });
      
      // Desconectar datos de salud en el servicio
      agtfitService.disconnectHealthData();
    } catch (err) {
      console.error('Error disconnecting health data:', err);
      setError('An error occurred while disconnecting from health data.');
    } finally {
      setLoading(false);
    }
  };
  
  // Formatear las listas de elementos (alergias, medicamentos, etc.)
  const formatList = (list) => {
    if (!list || !Array.isArray(list) || list.length === 0) {
      return 'None';
    }
    
    return list.map(item => {
      // Si es un objeto con propiedad S, usar esa propiedad
      if (item && typeof item === 'object' && 'S' in item) {
        return capitalizeFirstLetter(item.S);
      }
      return capitalizeFirstLetter(item);
    }).join(', ');
  };
  
  // Renderizar datos de salud
  const renderHealthData = () => {
    if (!healthData) return null;
    
    const { patient, records } = healthData;
    
    // Extraer signos vitales del registro más reciente
    const latestVitalSigns = getLatestVitalSigns(records);
    
    return (
      <DataContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box 
              sx={{ 
                width: 46, 
                height: 46, 
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: GRADIENT,
                mr: 2,
                boxShadow: '0 4px 10px rgba(255,107,0,0.2)',
              }}
            >
              <User size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {`${patient.firstName} ${patient.lastName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${patient.age} years / ${patient.gender === 'male' ? 'Male' : 'Female'}`}
              </Typography>
            </Box>
          </Box>
          
          <OutlinedButton
            variant="outlined"
            startIcon={<Unlink size={16} />}
            onClick={handleDisconnect}
            disabled={loading}
            sx={{
              mt: 0,
              borderRadius: '12px',
              py: 1
            }}
          >
            Disconnect Health Data
          </OutlinedButton>
        </Box>
        
        <InfoCard elevation={0}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Blood Type
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {patient.bloodType || 'Not specified'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  BMI
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {patient.bmi ? `${patient.bmi} kg/m²` : 'Not calculated'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Height
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {patient.height ? `${patient.height} cm` : 'Not specified'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Weight
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {patient.weight ? `${patient.weight} kg` : 'Not specified'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </InfoCard>
        
        <SectionTitle>
          <Activity size={20} />
          Vital Signs
        </SectionTitle>
        
        <Grid container spacing={2.5}>
          {(latestVitalSigns.heartRate || patient.heartRate) && (
            <Grid item xs={12} sm={6} md={4}>
              <VitalSign>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #FF5252 0%, #FF3B30 100%)',
                  boxShadow: '0 3px 8px rgba(255,59,48,0.2)',
                  mr: 2
                }}>
                  <Heart size={20} color="#FFFFFF" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Heart Rate
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {`${latestVitalSigns.heartRate || patient.heartRate} bpm`}
                  </Typography>
                </Box>
              </VitalSign>
            </Grid>
          )}
          
          {((latestVitalSigns.systolicBP && latestVitalSigns.diastolicBP) || 
            (patient.systolicBP && patient.diastolicBP)) && (
            <Grid item xs={12} sm={6} md={4}>
              <VitalSign>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: GRADIENT,
                  boxShadow: '0 3px 8px rgba(255,107,0,0.2)',
                  mr: 2
                }}>
                  <Activity size={20} color="#FFFFFF" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Blood Pressure
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {`${latestVitalSigns.systolicBP || patient.systolicBP}/${latestVitalSigns.diastolicBP || patient.diastolicBP} mmHg`}
                  </Typography>
                </Box>
              </VitalSign>
            </Grid>
          )}
          
          {(latestVitalSigns.oxygenSaturation || patient.oxygenSaturation) && (
            <Grid item xs={12} sm={6} md={4}>
              <VitalSign>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #2196F3 0%, #007AFF 100%)',
                  boxShadow: '0 3px 8px rgba(0,122,255,0.2)',
                  mr: 2
                }}>
                  <Droplet size={20} color="#FFFFFF" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Oxygen Saturation
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {`${latestVitalSigns.oxygenSaturation || patient.oxygenSaturation}%`}
                  </Typography>
                </Box>
              </VitalSign>
            </Grid>
          )}
          
          {(latestVitalSigns.temperature || patient.temperature) && (
            <Grid item xs={12} sm={6} md={4}>
              <VitalSign>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #FFB74D 0%, #FFA726 100%)',
                  boxShadow: '0 3px 8px rgba(255,167,38,0.2)',
                  mr: 2
                }}>
                  <Thermometer size={20} color="#FFFFFF" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Temperature
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {`${latestVitalSigns.temperature || patient.temperature}°C`}
                  </Typography>
                </Box>
              </VitalSign>
            </Grid>
          )}
          
          {(latestVitalSigns.bloodGlucose || patient.bloodGlucose) && (
            <Grid item xs={12} sm={6} md={4}>
              <VitalSign>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #34C759 0%, #32D74B 100%)',
                  boxShadow: '0 3px 8px rgba(52,199,89,0.2)',
                  mr: 2
                }}>
                  <Droplet size={20} color="#FFFFFF" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Blood Glucose
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {`${latestVitalSigns.bloodGlucose || patient.bloodGlucose} mg/dL`}
                  </Typography>
                </Box>
              </VitalSign>
            </Grid>
          )}
          
          {/* Si no hay signos vitales disponibles */}
          {!latestVitalSigns.heartRate && !patient.heartRate &&
           !latestVitalSigns.systolicBP && !patient.systolicBP &&
           !latestVitalSigns.oxygenSaturation && !patient.oxygenSaturation &&
           !latestVitalSigns.temperature && !patient.temperature &&
           !latestVitalSigns.bloodGlucose && !patient.bloodGlucose && (
            <Grid item xs={12}>
              <Box sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: '16px',
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.bgLight
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'),
                backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                border: `1px solid ${theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.border
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
              }}>
                <Typography variant="body2" color="text.secondary">
                  No vital signs data available
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        
        <SectionTitle>
          <Stethoscope size={20} />
          Medical Information
        </SectionTitle>
        
        <InfoCard elevation={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Allergies
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatList(patient.allergies)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Medications
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatList(patient.medications)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Chronic Diseases
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatList(patient.chronicDiseases)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Current Pathologies
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatList(patient.currentPathologies)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </InfoCard>
        
        {records && records.length > 0 && (
          <>
            <SectionTitle>
              <Clock size={20} />
              Medical Records ({records.length})
            </SectionTitle>

            <Box sx={{
              borderRadius: '16px',
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.bgLight
                : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'),
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              border: `1px solid ${theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.border
                : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
              overflow: 'hidden'
            }}>
              <List disablePadding>
                {records.slice(0, 3).map((record, index) => (
                  <ListItem
                    key={record.recordId}
                    divider={index < Math.min(records.length, 3) - 1}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255,255,255,0.08)'
                          : (theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.02)'),
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {new Date(record.date).toLocaleDateString()}
                          </Typography>
                          <Chip 
                            label={
                              record.recordType === "fitness_check_in" 
                                ? "Fitness Check-In" 
                                : (record.diagnosis ? 'Diagnosis' : 'Check-up')
                            } 
                            size="small"
                            sx={{
                              borderRadius: '8px',
                              fontWeight: 500,
                              backgroundColor: record.recordType === "fitness_check_in"
                                ? 'rgba(52,199,89,0.1)'
                                : record.diagnosis 
                                  ? 'rgba(255,107,0,0.1)'
                                  : theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.1)'
                                    : 'rgba(0,0,0,0.05)',
                              color: record.recordType === "fitness_check_in"
                                ? '#34C759'
                                : record.diagnosis 
                                  ? FIT_ORANGE
                                  : theme.palette.text.secondary,
                              border: `1px solid ${
                                record.recordType === "fitness_check_in"
                                  ? 'rgba(52,199,89,0.2)'
                                  : record.diagnosis 
                                    ? 'rgba(255,107,0,0.2)'
                                    : 'transparent'
                              }`,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {record.notes?.slice(0, 100) || 'No notes'}
                          {record.notes?.length > 100 ? '...' : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              
              {records.length > 3 && (
                <Box sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  borderTop: `1px solid ${theme.palette.divider}`
                }}>
                  <Button
                    sx={{
                      color: FIT_ORANGE,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255,107,0,0.15)'
                          : (theme.palette.mode === 'dark'
                            ? 'rgba(255,107,0,0.1)'
                            : 'rgba(255,107,0,0.05)')
                      }
                    }}
                  >
                    View All {records.length} Records
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </DataContainer>
    );
  };
 
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <StatsCard>
          <TitleText variant="h5">Health Metrics by AGT Health+</TitleText>
         
          <ContentContainer>
            {/* Estado de carga del perfil */}
            {profileLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                <AppleSpinner size={30} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Checking connection status...
                </Typography>
              </Box>
            )}
            
            {/* Estado desconectado */}
            {!profileLoading && !loading && !healthData && !isConnected && (
              <>
                <Box sx={{ 
                  maxWidth: '90%',
                  mx: 'auto',
                  mb: 5,
                  mt: 2
                }}>
                  <Box 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: GRADIENT,
                      mx: 'auto',
                      mb: 3,
                      boxShadow: '0 8px 20px rgba(255,107,0,0.25)',
                    }}
                  >
                    <Heart size={36} color="#FFFFFF" />
                  </Box>
                  
                  <Typography 
                    variant="h4" 
                    align="center" 
                    sx={{ 
                      fontWeight: 700,
                      mb: 2,
                      background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Connect Your Health Data
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    align="center" 
                    color="text.secondary"
                    sx={{ mb: 4 }}
                  >
                    Sync your fitness data with your health records in one tap.
                    <Box component="span" sx={{ fontWeight: 600, display: 'block', mt: 1 }}>
                      Authorized healthcare providers can view this information.
                    </Box>
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <GradientButton
                    variant="contained"
                    startIcon={<LinkIcon size={18} />}
                    onClick={handleConnect}
                    disabled={loading}
                  >
                    Connect
                    <Box component="span" sx={{ fontSize: '14px', ml: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'top', position: 'relative', top: -5 }}>
                      <Plus size={14} />
                    </Box>
                  </GradientButton>
                </Box>
                
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 3, 
                      width: '100%',
                      borderRadius: '12px',
                      '& .MuiAlert-icon': {
                        alignItems: 'center'
                      }
                    }}
                  >
                    {error}
                  </Alert>
                )}
              </>
            )}
            
            {/* Estado de carga */}
            {!profileLoading && loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                <AppleSpinner size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {isConnected ? 'Loading your health data...' : 'Connecting to your health data...'}
                </Typography>
              </Box>
            )}
            
            {/* Mostrar datos de salud cuando están disponibles */}
            {!profileLoading && !loading && healthData && renderHealthData()}
          </ContentContainer>
        </StatsCard>
      </Grid>
    </Grid>
  );
};

export default Health;